// src/components/common/LightboxModal.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha } from "@mui/material/styles";

function normalizeMediaKind(media) {
	const type = String(media?.type || "").toLowerCase();
	return type.startsWith("video") ? "video" : "image";
}

export function LightboxModal({
	media,
	items = [],
	initialIndex = 0,
	open,
	onClose,
}) {
	const [zoomed, setZoomed] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);
	const touchStartXRef = useRef(null);

	const hasItems = items.length > 0;
	const maxIndex = Math.max(items.length - 1, 0);

	useEffect(() => {
		if (!open) return;
		setCurrentIndex(Math.max(0, Math.min(initialIndex, maxIndex)));
	}, [initialIndex, maxIndex, open]);

	const activeMedia = useMemo(() => {
		if (hasItems) {
			return items[Math.max(0, Math.min(currentIndex, maxIndex))] || null;
		}
		return media || null;
	}, [currentIndex, hasItems, items, maxIndex, media]);

	useEffect(() => {
		if (!open) setZoomed(false);
	}, [open, activeMedia?.url]);

	const canNavigate = hasItems && items.length > 1 && !zoomed;
	const goPrev = useCallback(() => {
		if (!canNavigate) return;
		setZoomed(false);
		setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
	}, [canNavigate, maxIndex]);
	const goNext = useCallback(() => {
		if (!canNavigate) return;
		setZoomed(false);
		setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
	}, [canNavigate, maxIndex]);

	useEffect(() => {
		if (!open || !canNavigate) return undefined;
		const handleKeyDown = (event) => {
			if (event.key === "ArrowLeft") goPrev();
			if (event.key === "ArrowRight") goNext();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [canNavigate, goNext, goPrev, open]);

	return (
		<Modal
			open={open}
			onClose={onClose}
			closeAfterTransition
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Box
				sx={{
					position: "relative",
					bgcolor: (theme) => alpha(theme.palette.common.black, 0.92),
					outline: "none",
					width: "100vw",
					height: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					overflow: zoomed ? "auto" : "hidden",
				}}
			>
				<IconButton
					onClick={onClose}
					sx={{
						position: "fixed",
						top: 10,
						right: 10,
						zIndex: 1301,
						backgroundColor: (theme) => alpha(theme.palette.common.black, 0.35),
						color: "common.white",
						"&:hover": { backgroundColor: (theme) => alpha(theme.palette.common.black, 0.5) },
					}}
				>
					<CloseIcon />
				</IconButton>

				{canNavigate && (
					<>
						<IconButton
							onClick={goPrev}
							sx={{
								position: "fixed",
								left: 10,
								top: "50%",
								transform: "translateY(-50%)",
								zIndex: 1301,
								backgroundColor: (theme) => alpha(theme.palette.common.black, 0.35),
								color: "common.white",
								"&:hover": { backgroundColor: (theme) => alpha(theme.palette.common.black, 0.5) },
							}}
						>
							<ChevronLeftIcon />
						</IconButton>
						<IconButton
							onClick={goNext}
							sx={{
								position: "fixed",
								right: 10,
								top: "50%",
								transform: "translateY(-50%)",
								zIndex: 1301,
								backgroundColor: (theme) => alpha(theme.palette.common.black, 0.35),
								color: "common.white",
								"&:hover": { backgroundColor: (theme) => alpha(theme.palette.common.black, 0.5) },
							}}
						>
							<ChevronRightIcon />
						</IconButton>
					</>
				)}

				{activeMedia && normalizeMediaKind(activeMedia) === "image" ? (
					<img
						src={activeMedia?.url}
						alt="lightbox"
						onClick={() => setZoomed((z) => !z)}
						onTouchStart={(event) => {
							touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
						}}
						onTouchEnd={(event) => {
							if (!canNavigate) return;
							const startX = touchStartXRef.current;
							const endX = event.changedTouches?.[0]?.clientX ?? null;
							touchStartXRef.current = null;
							if (!Number.isFinite(startX) || !Number.isFinite(endX)) return;
							const deltaX = endX - startX;
							if (Math.abs(deltaX) < 50) return;
							if (deltaX < 0) goNext();
							else goPrev();
						}}
						style={{
							maxHeight: "94vh",
							maxWidth: "96vw",
							width: "auto",
							height: "auto",
							objectFit: "contain",
							display: "block",
							margin: "auto",
							transform: zoomed ? "scale(1.8)" : "scale(1)",
							transformOrigin: "center center",
							transition: "transform 220ms ease",
							cursor: zoomed ? "zoom-out" : "zoom-in",
						}}
					/>
				) : activeMedia ? (
					<video
						src={activeMedia.url}
						controls
						playsInline
						preload="auto"
						disablePictureInPicture
						controlsList="nofullscreen"
						onTouchStart={(event) => {
							touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
						}}
						onTouchEnd={(event) => {
							if (!canNavigate) return;
							const startX = touchStartXRef.current;
							const endX = event.changedTouches?.[0]?.clientX ?? null;
							touchStartXRef.current = null;
							if (!Number.isFinite(startX) || !Number.isFinite(endX)) return;
							const deltaX = endX - startX;
							if (Math.abs(deltaX) < 50) return;
							if (deltaX < 0) goNext();
							else goPrev();
						}}
						style={{
							maxHeight: "94vh",
							maxWidth: "96vw",
							cursor: "pointer",
							display: "block",
							margin: "auto",
						}}
					/>
				) : null}
			</Box>
		</Modal>
	);
}
